/**
 * Quies Product Configuration
 *
 * Defines all quies-specific settings, services, branding, and features.
 */

import type {FunnelOverrides} from '@core/products/funnel'

export const quiesConfig = {
  id: 'quies',
  name: 'Quies.ai',
  domain: 'quies.ai',

  // SEO & Meta
  meta: {
    // The fallback for a page without its own title: the brand alone, so it never competes with
    // the home for the home's query.
    title: 'Quies.ai',
    description:
      'Quies writes a self-hypnosis session for how you feel today, in your language. Different every time.',
    url: 'https://quies.ai',
    keywords:
      'self-hypnosis, AI hypnosis, Ericksonian hypnosis, hypnotic session, guided self-hypnosis, AI-powered hypnotherapy, deep relaxation, hypnotic visualization, stress relief, inner work',
    ogImage: 'https://quies.ai/quies-og.jpg',
    ogImageWidth: 1200,
    ogImageHeight: 630,
    themeColor: '#0D1119', // --qs-color-ground
    colorScheme: 'dark' as const,
  },

  // Backend Services — internal service addresses. Server-only: @product/quies/config.server.ts
  // (quiesServerConfig.services). Never on this object — quies/config.ts is imported directly by
  // client components and this whole module is serialized into every page's hydration script by
  // root.tsx's loader.

  // Contact
  // Sign-in methods beside Google and the email link (`@core/auth/signInMethods`).
  signIn: {facebook: true, googleOneTap: true},

  contact: {
    email: 'contact@quies.ai',
  },

  // Funnel — overrides only (defaults in core/products/funnel.ts)
  funnel: {
    // A guest holds no credit; sign-up grants the two that pay for the first sessions.
    freeTokens: 0,
    signupBonus: 2,
    unit: {singular: 'session', plural: 'sessions'},
    cta: {
      purchase: 'Select Plan',
      signup: 'Continue with Google',
      gateTitle: 'Continue your self-hypnosis',
      gateMessage: 'Create an account to unlock more sessions and preserve your progress.',
    },
    insufficientTokensStatus: 403,
  } satisfies FunnelOverrides,

  // Session cookie domain is server-only: @product/quies/config.server.ts
  // (quiesServerConfig.session), same reason as `services` above.
} as const

export type QuiesConfig = typeof quiesConfig
